import { useMemo } from 'react';
import styled from '@emotion/styled';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import type { MarketingMessage } from '@trinity/chat';

export interface MessageGenerationTableProps {
  messages: MarketingMessage[];
  isGenerating: boolean;
  sessionId?: number;
  numberOfMessages?: number;
  onSave?: () => void;
  isSaving?: boolean;
}

export function MessageGenerationTable({
  messages,
  isGenerating,
  sessionId,
  numberOfMessages = 5,
  onSave,
  isSaving = false,
}: MessageGenerationTableProps): React.JSX.Element {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        width: 200,
        flex: 1,
        renderCell: (params) => <TitleCell>{params.value}</TitleCell>,
      },
      {
        field: 'body',
        headerName: 'Message',
        width: 350,
        flex: 2,
        renderCell: (params) => <MessageCell>{params.value}</MessageCell>,
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 150,
        renderCell: (params) => <CategoryBadge>{params.value}</CategoryBadge>,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <StatusBadge status={params.value as 'draft' | 'approved' | 'published'}>
            {params.value}
          </StatusBadge>
        ),
      },
    ],
    []
  );

  // Create rows with id field required by DataGrid
  const rows = useMemo(
    () =>
      messages.map((msg, index) => ({
        ...msg,
        id: msg.id || index, // Use message id or fallback to index
      })),
    [messages]
  );

  // Generate skeleton rows while loading
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: Math.min(numberOfMessages, 10) }).map((_, index) => ({
        id: `skeleton-${index}`,
        title: '',
        body: '',
        category: '',
        status: 'draft' as const,
      })),
    [numberOfMessages]
  );

  return (
    <Root>
      <PanelHeader>
        <HeaderLeft>
          <HeaderTitle>Generated Messages</HeaderTitle>
          <MessageCount>{messages.length} messages</MessageCount>
        </HeaderLeft>
        {messages.length > 0 && onSave && (
          <SaveButton onClick={onSave} disabled={isSaving || !sessionId}>
            {isSaving ? 'Saving...' : 'Save Messages'}
          </SaveButton>
        )}
      </PanelHeader>

      <DataGridContainer>
        <DataGrid
          rows={isGenerating ? skeletonRows : rows}
          columns={columns}
          loading={isGenerating}
          disableRowSelectionOnClick
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { fileName: `messages-session-${sessionId}` },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontSize: '11px',
              fontWeight: 600,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              color: '#424242',
              borderBottom: '1px solid #e8e8e8',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px 16px',
              borderBottom: '1px solid #e0e0e0',
              gap: '8px',
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
          }}
        />
      </DataGridContainer>
    </Root>
  );
}

// Styled Components
const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const HeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.disabled ? '#a0a0a0' : props.theme.palette.sentiment.primary.main)};
  border: none;
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.palette.sentiment.primary.dark};
  }
`;

const MessageCount = styled.span`
  font-size: 13px;
  color: #666;
  padding: 4px 10px;
  background: #f5f5f5;
  border-radius: 10px;
`;

const DataGridContainer = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) => props.theme.palette.sentiment.primary.lighter};
  color: ${(props) => props.theme.palette.sentiment.primary.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;
